/**
 * 契約一覧の状態と操作（プロダクト仕様書「契約一覧」）。
 *
 * 指定した月・検索・お気に入りの絞り込みは URL のクエリに持ち、契約詳細から戻ったときに、移る前のままにする。
 * クエリにお気に入りの絞り込みがないとき（サイドバーから開いたとき）は、お気に入りを1つ以上登録している利用者には、
 * お気に入りの企業だけに絞り込んで表示する。
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { toast } from 'sonner';

import { useAuthHeader } from '@/hooks/useAuth';
import * as api from '@/lib/api/credit-contracts';
import type { CompanySummary, ContractList } from '@/lib/api/credit-contracts';
import { isMonth } from '@/lib/credit-contracts/month';
import { trimWhiteSpace } from '@/lib/credit-conversion-settings/number';

import { fetchFailureMessage } from './display';

export type Load<T> =
  | { status: 'loading' }
  | { status: 'ok'; data: T }
  | { status: 'error'; message: string };

/** 企業名か企業IDの一部で絞り込む。大文字と小文字は区別しない */
export function matches(company: CompanySummary, query: string): boolean {
  const needle = trimWhiteSpace(query).toLowerCase();
  if (!needle) return true;
  return company.name.toLowerCase().includes(needle) || company.id.toLowerCase().includes(needle);
}

export function useContractList() {
  const { getAuthHeader } = useAuthHeader();
  const [searchParams, setSearchParams] = useSearchParams();
  // 取得の途中で関数が作り直されても、走っている取得を続けられるよう参照で持つ
  const authRef = useRef(getAuthHeader);
  authRef.current = getAuthHeader;
  const setParamsRef = useRef(setSearchParams);
  setParamsRef.current = setSearchParams;
  // 画面が最後に書いた URL のクエリ。これと違うクエリになったら、画面の外から URL が変わった
  const lastSearch = useRef<string | null>(null);

  const [list, setList] = useState<Load<ContractList>>({ status: 'loading' });
  // 表示している値があるときの取得中。取得し終えるまで前の値の表を残し、表のスクロールの位置を保つ
  const [refreshing, setRefreshing] = useState(false);
  const [query, setQueryState] = useState('');
  const [favoritesOnly, setFavoritesOnlyState] = useState(false);
  const [toggling, setToggling] = useState<ReadonlySet<string>>(new Set());
  // 後から始めた取得の結果だけを使う
  const sequence = useRef(0);
  // 取得とお気に入りの登録・解除の前後を比べる時計。取得を始めた後に登録・解除し終えた企業は、取得の結果より登録・解除を優先する
  const clock = useRef(0);
  const favoriteWrites = useRef(new Map<string, { favorite: boolean; at: number }>());
  // URL に書く値は、取得の結果を待つ間も最新のものを使う
  const state = useRef({ month: null as string | null, query: '', favoritesOnly: false });
  // URL にお気に入りの絞り込みがなく、まだ取得の結果から決めていない
  const favoritesUndecided = useRef(false);

  const writeParams = () => {
    const { month, query: q, favoritesOnly: fav } = state.current;
    const params = new URLSearchParams();
    if (month) params.set('month', month);
    if (q) params.set('q', q);
    params.set('favorites', fav ? '1' : '0');
    lastSearch.current = params.toString();
    setParamsRef.current(params, { replace: true });
  };

  /** 指定した月の値を取得する。お気に入りの絞り込みが決まっていなければ、取得した結果から決める */
  const fetchMonth = useCallback(async (month: string | null) => {
    const id = ++sequence.current;
    const startedAt = ++clock.current;
    setList((current) => (current.status === 'ok' ? current : { status: 'loading' }));
    setRefreshing(true);
    try {
      const fetched = await api.getContracts(month, authRef.current());
      if (id !== sequence.current) return;
      // 取得がお気に入りの登録・解除より前の状態を読んでいても、星を元に戻さない
      const data = {
        ...fetched,
        companies: fetched.companies.map((c) => {
          const write = favoriteWrites.current.get(c.id);
          return write && write.at > startedAt ? { ...c, favorite: write.favorite } : c;
        }),
      };
      state.current.month = data.month;
      if (favoritesUndecided.current) {
        favoritesUndecided.current = false;
        const fav = data.companies.some((c) => c.favorite);
        state.current.favoritesOnly = fav;
        setFavoritesOnlyState(fav);
      }
      setList({ status: 'ok', data });
      setRefreshing(false);
      writeParams();
    } catch (e) {
      if (id !== sequence.current) return;
      setList({ status: 'error', message: fetchFailureMessage(api.toFailure(e)) });
      setRefreshing(false);
    }
  }, []);

  /** URL のクエリから始める（最初の表示と、画面の外から URL が変わったとき） */
  const startFrom = useCallback(
    (params: URLSearchParams) => {
      const month = params.get('month');
      const q = params.get('q') ?? '';
      const fav = params.get('favorites');
      state.current = {
        month: month && isMonth(month) ? month : null,
        query: q,
        favoritesOnly: fav === '1',
      };
      favoritesUndecided.current = fav === null;
      setQueryState(q);
      setFavoritesOnlyState(fav === '1');
      void fetchMonth(state.current.month);
    },
    [fetchMonth],
  );

  useEffect(() => {
    const current = searchParams.toString();
    if (current === lastSearch.current) return;
    lastSearch.current = current;
    startFrom(searchParams);
  }, [searchParams, startFrom]);

  const selectMonth = (month: string) => {
    state.current.month = month;
    void fetchMonth(month);
  };

  const reload = () => void fetchMonth(state.current.month);

  const setQuery = (q: string) => {
    state.current.query = q;
    setQueryState(q);
    writeParams();
  };

  const setFavoritesOnly = (fav: boolean) => {
    favoritesUndecided.current = false;
    state.current.favoritesOnly = fav;
    setFavoritesOnlyState(fav);
    writeParams();
  };

  const toggleFavorite = async (company: CompanySummary) => {
    setToggling((s) => new Set(s).add(company.id));
    const next = !company.favorite;
    try {
      if (next) await api.addFavorite(company.id, authRef.current());
      else await api.removeFavorite(company.id, authRef.current());
      favoriteWrites.current.set(company.id, { favorite: next, at: ++clock.current });
      setList((current) =>
        current.status === 'ok'
          ? {
              ...current,
              data: {
                ...current.data,
                companies: current.data.companies.map((c) =>
                  c.id === company.id ? { ...c, favorite: next } : c,
                ),
              },
            }
          : current,
      );
    } catch (e) {
      const reason = api.toFailure(e).message.replace(/。$/, '');
      toast.error(
        `${company.name}を、お気に入り${next ? 'に登録' : 'から外すことが'}できませんでした（${reason}）。`,
      );
    } finally {
      setToggling((s) => {
        const rest = new Set(s);
        rest.delete(company.id);
        return rest;
      });
    }
  };

  const shown = useMemo(
    () =>
      list.status === 'ok'
        ? list.data.companies.filter((c) => (!favoritesOnly || c.favorite) && matches(c, query))
        : [],
    [list, favoritesOnly, query],
  );

  return {
    list,
    refreshing: refreshing && list.status === 'ok',
    shown,
    query,
    favoritesOnly,
    toggling,
    selectMonth,
    reload,
    setQuery,
    setFavoritesOnly,
    toggleFavorite,
  };
}
